import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const accidentSteps = [
    {
        id: 'step-1',
        title: 'Step 1: Check for Injuries and Move Safely',
        text: 'The first priority is people, not the car. Check yourself and your passengers for injuries. If anyone is hurt, do not try to move them unless there is an immediate danger (like a fire). Turn on your hazard lights and, if the vehicle is drivable and safe, move it to the shoulder to avoid secondary collisions.'
    },
    {
        id: 'step-2',
        title: 'Step 2: Call Emergency Services',
        text: 'Call 911 if there are injuries, significant property damage, or if the other driver is uncooperative or impaired. Having a police officer document the scene is crucial for insurance claims, as they will provide a neutral incident report that states the facts of the event.'
    },
    {
        id: 'step-3',
        title: 'Step 3: Exchange Details and Document the Scene',
        text: 'Exchange names, contact info, and insurance details with everyone involved. Use your phone to take wide-angle and close-up photos of the damage, the street layout, traffic signs, and any skid marks. If there are witnesses, get their names and numbers before they leave.'
    },
    {
        id: 'step-4',
        title: 'Step 4: Notify Insurance and Record Everything',
        text: 'Report the accident to your insurer as soon as possible. Keep a file of all related documents: police reports, medical bills, tow receipts, and repair estimates. A clear, chronological record will make the claims process much smoother and protect you from liability disputes.'
    }
];

const faqs = [
    {
        question: 'Should I call the police for a minor accident?',
        answer: 'Yes, it’s usually best to have a police report on file. Even if the damage seems minor, hidden issues can appear later, and having an official report protects you during an insurance claim.'
    },
    {
        question: 'What if the other driver refuses to share insurance details?',
        answer: 'Do not escalate the situation. Take a photo of their car, license plate, and the driver themselves if safe. Provide this information to the police and your insurance company immediately.'
    },
    {
        question: 'Should I admit fault at the scene?',
        answer: 'Never. Fault is a legal determination. Simply state the facts of what happened to the officer. Admitting fault at the scene can complicate your insurance coverage and legal standing later.'
    },
    {
        question: 'What photos matter most after a crash?',
        answer: 'Capture all points of contact on the vehicles, the positions of the cars relative to the road, any visible injuries, debris, and the surrounding environment (weather, signs, lights).'
    }
];

const Blog20 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 20;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5 z-50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            What to Do After a Car Accident: Step-by-Step Guide for Drivers
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            Right after a crash, it's hard to think clearly. Having a simple plan protects your safety, your documentation, and your insurance claim.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated April 2026 &nbsp;|&nbsp; 6 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/20_1.jpeg"
                            alt="Driver dealing with a car accident and emergency situation"
                            title="What to Do After a Car Accident"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: accident_emergency_response.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#step-1" className="text-gray-900 no-underline hover:underline">Step 1: Move safely</a></li>
                            <li><a href="#step-2" className="text-gray-900 no-underline hover:underline">Step 2: Emergency services</a></li>
                            <li><a href="#step-3" className="text-gray-900 no-underline hover:underline">Step 3: Document scene</a></li>
                            <li><a href="#step-4" className="text-gray-900 no-underline hover:underline">Step 4: Insurance</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            Right after a crash, most people do not think clearly. The problem is that those first few minutes matter for safety, documentation, and insurance. A simple step-by-step plan makes it much easier to do the right things in the right order.
                        </p>
                        <p className="mb-0 text-gray-700">
                            The safest first move is to protect people first, then document the scene clearly before details are lost. Even a minor-looking accident can leave someone shaken or in danger from active traffic.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>What to do first?</strong><br />
                            Check for injuries and move to a safe location. Call 911 if needed, document the scene with photos, exchange info with other drivers, and notify your insurance company as soon as possible.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/20_2.jpeg"
                            alt="Driver documenting a car accident and speaking with responders"
                            title="Documenting an accident properly"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: documenting_the_scene.png
                        </figcaption>
                    </figure>

                    {accidentSteps.map((step) => (
                        <section key={step.id} id={step.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {step.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{step.text}</p>
                        </section>
                    ))}

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Accidents are stressful because details get scattered quickly. CarFido Assistant helps you stay organized when it matters most.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Follow a guided emergency checklist right at the scene</li>
                                <li>Securely store accident photos, witness details, and insurance info in your digital glove box</li>
                                <li>Log the incident in your vehicle's permanent maintenance and history record</li>
                                <li>Track insurance claim follow-ups and repair estimates in one central location</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            The best response after a car accident is a calm one with a clear order. Safety comes first, documentation comes next, and good record-keeping helps with everything that follows.
                        </p>
                        <p className="mb-0 text-gray-700">
                            You don't need to remember everything perfectly in the moment. Just follow the process to protect people, preserve the facts, and keep your paperwork organized. A few careful steps make the next few weeks much easier.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#step-1" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Step 1: Safety</a></li>
                            <li><a href="#step-2" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Step 2: Emergency</a></li>
                            <li><a href="#step-3" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Step 3: Document</a></li>
                            <li><a href="#step-4" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Step 4: Insurance</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog20;
